/* eslint-disable jsdoc/require-jsdoc */
import { httpClient } from "../../../lib/http/axios";
import type { ApiEnvelope } from "../../../lib/http/contracts";
import {
  mapDimHousLevelOption,
  mapGroundOption,
  mapGuideProviderOption,
  mapSupplierItem,
  mapSupplierListPage,
  mapSupplierListQueryParams,
  mapSupplierMutationPayload,
  mapUserOption,
  type SupplierItemRaw,
  type GuideProviderOptionRaw,
  type SupplierListPageRaw,
} from "../api/adapters";
import { supplierKindMeta } from "../config";
import type {
  DimHousLevelOption,
  GroundOption,
  GuideProviderOption,
  SupplierAttachmentMutationInput,
  SupplierAttachmentRecord,
  SupplierBankAccountMutation,
  SupplierBankAccountRecord,
  SupplierBusCapabilityMutation,
  SupplierBusCapabilityRecord,
  SupplierContactMutationInput,
  SupplierContactRecord,
  SupplierGuideRelationMutation,
  SupplierGuideRelationRecord,
  SupplierItem,
  SupplierKind,
  SupplierListPage,
  SupplierListParams,
  SupplierMutation,
  SupplierQuoteCapabilityMutation,
  SupplierQuoteCapabilityRecord,
  SupplierStayCapabilityRecord,
  SupplierTimedCapabilityMutation,
  UserOption,
} from "../types";

/** 分页查询品类供应商。 */
export async function fetchSupplierList(
  kind: SupplierKind,
  params: SupplierListParams,
): Promise<SupplierListPage> {
  const segment = supplierKindMeta[kind].apiSegment;
  const response = await httpClient.get<ApiEnvelope<SupplierListPageRaw>>(
    `/suppliers/${segment}`,
    { params: mapSupplierListQueryParams(params) },
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("供应商列表接口返回了无效数据");
  }
  return mapSupplierListPage(response.data.data);
}

/** 创建品类供应商。 */
export async function createSupplier(
  kind: SupplierKind,
  payload: SupplierMutation,
): Promise<SupplierItem> {
  const segment = supplierKindMeta[kind].apiSegment;
  const response = await httpClient.post<ApiEnvelope<SupplierItemRaw>>(
    `/suppliers/${segment}`,
    mapSupplierMutationPayload(kind, payload),
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("供应商创建接口返回了无效数据");
  }
  return mapSupplierItem(response.data.data);
}

/** 更新品类供应商。 */
export async function updateSupplier(
  kind: SupplierKind,
  supplierId: string,
  payload: SupplierMutation,
): Promise<SupplierItem> {
  const segment = supplierKindMeta[kind].apiSegment;
  const response = await httpClient.put<ApiEnvelope<SupplierItemRaw>>(
    `/suppliers/${segment}/${supplierId}`,
    mapSupplierMutationPayload(kind, payload),
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("供应商更新接口返回了无效数据");
  }
  return mapSupplierItem(response.data.data);
}

/** 停用。 */
export async function disableSupplier(
  kind: SupplierKind,
  supplierId: string,
): Promise<SupplierItem> {
  const segment = supplierKindMeta[kind].apiSegment;
  const response = await httpClient.post<ApiEnvelope<SupplierItemRaw>>(
    `/suppliers/${segment}/${supplierId}/disable`,
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("供应商停用接口返回了无效数据");
  }
  return mapSupplierItem(response.data.data);
}

/** 启用。 */
export async function enableSupplier(
  kind: SupplierKind,
  supplierId: string,
): Promise<SupplierItem> {
  const segment = supplierKindMeta[kind].apiSegment;
  const response = await httpClient.post<ApiEnvelope<SupplierItemRaw>>(
    `/suppliers/${segment}/${supplierId}/enable`,
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("供应商启用接口返回了无效数据");
  }
  return mapSupplierItem(response.data.data);
}

/** 住宿档次下拉。 */
export async function fetchDimHousLevels(): Promise<DimHousLevelOption[]> {
  const response = await httpClient.get<
    ApiEnvelope<
      Array<{
        hous_level_id: string;
        level_code: string;
        level_name: string;
        level_short_name?: string | null;
      }>
    >
  >("/suppliers/dim-hous-levels");
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("住宿档次接口返回了无效数据");
  }
  return response.data.data.map(mapDimHousLevelOption);
}

/** 地接下拉。 */
export async function fetchGroundOptions(q?: string): Promise<GroundOption[]> {
  const response = await httpClient.get<
    ApiEnvelope<
      Array<{
        supplier_id: string;
        supplier_name: string;
        supplier_short_name?: string | null;
      }>
    >
  >("/suppliers/ground-options", { params: { q: q?.trim() || undefined, limit: 50 } });
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("地接选项接口返回了无效数据");
  }
  return response.data.data.map(mapGroundOption);
}

/** 花儿用户下拉（导游绑定）。 */
export async function fetchUserOptions(q?: string): Promise<UserOption[]> {
  const response = await httpClient.get<
    ApiEnvelope<
      Array<{
        user_id: number;
        display_name: string;
        phone?: string | null;
      }>
    >
  >("/suppliers/user-options", { params: { q: q?.trim() || undefined, limit: 50 } });
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("用户选项接口返回了无效数据");
  }
  return response.data.data.map(mapUserOption);
}

/** 使用供应商附件权限上传图片或文件，返回可展示 URL。 */
export async function uploadSupplierMedia(
  file: File,
  kind: "image" | "file" = "image",
): Promise<string> {
  const form = new FormData();
  form.append("file", file);
  const response = await httpClient.post<ApiEnvelope<{ file_url: string }>>(
    `/suppliers/media/upload?kind=${encodeURIComponent(kind)}`,
    form,
    { "axios-retry": { retries: 0 } },
  );
  if (response.data.code !== "OK" || !response.data.data?.file_url) {
    throw new Error("供应商附件上传接口返回了无效数据");
  }
  return response.data.data.file_url;
}

/** 导游提供或派遣主体下拉。 */
export async function fetchGuideProviderOptions(
  currentGuideSupplierId: string,
  q?: string,
): Promise<GuideProviderOption[]> {
  const response = await httpClient.get<
    ApiEnvelope<GuideProviderOptionRaw[]>
  >("/suppliers/guide-provider-options", {
    params: {
      current_guide_supplier_id: currentGuideSupplierId,
      q: q?.trim() || undefined,
      limit: 50,
    },
  });
  return unwrap(response.data, "导游提供方选项接口返回了无效数据").map(
    mapGuideProviderOption,
  );
}

type RawRecord = Record<string, unknown>;

/** 读取供应商联系人。 */
export async function fetchSupplierContacts(
  supplierId: string,
): Promise<SupplierContactRecord[]> {
  const response = await httpClient.get<ApiEnvelope<RawRecord[]>>(
    `/suppliers/${supplierId}/contacts`,
  );
  return unwrap(response.data, "供应商联系人接口返回了无效数据").map(mapContact);
}

export async function createSupplierContact(
  supplierId: string,
  payload: SupplierContactMutationInput,
): Promise<SupplierContactRecord> {
  const response = await httpClient.post<ApiEnvelope<RawRecord>>(
    `/suppliers/${supplierId}/contacts`,
    mapContactPayload(payload),
  );
  return mapContact(unwrap(response.data, "供应商联系人创建接口返回了无效数据"));
}

export async function updateSupplierContact(
  supplierId: string,
  contactId: string,
  payload: SupplierContactMutationInput,
): Promise<SupplierContactRecord> {
  const response = await httpClient.put<ApiEnvelope<RawRecord>>(
    `/suppliers/${supplierId}/contacts/${contactId}`,
    mapContactPayload(payload),
  );
  return mapContact(unwrap(response.data, "供应商联系人更新接口返回了无效数据"));
}

export async function disableSupplierContact(
  supplierId: string,
  contactId: string,
): Promise<SupplierContactRecord> {
  const response = await httpClient.post<ApiEnvelope<RawRecord>>(
    `/suppliers/${supplierId}/contacts/${contactId}/disable`,
  );
  return mapContact(unwrap(response.data, "供应商联系人停用接口返回了无效数据"));
}

/** 读取脱敏银行账户。 */
export async function fetchSupplierBankAccounts(
  supplierId: string,
): Promise<SupplierBankAccountRecord[]> {
  const response = await httpClient.get<ApiEnvelope<RawRecord[]>>(
    `/suppliers/${supplierId}/bank-accounts`,
  );
  return unwrap(response.data, "供应商银行账户接口返回了无效数据").map(mapBankAccount);
}

export async function createSupplierBankAccount(
  supplierId: string,
  payload: SupplierBankAccountMutation,
): Promise<SupplierBankAccountRecord> {
  const response = await httpClient.post<ApiEnvelope<RawRecord>>(
    `/suppliers/${supplierId}/bank-accounts`,
    mapBankAccountPayload(payload),
  );
  return mapBankAccount(unwrap(response.data, "供应商银行账户创建接口返回了无效数据"));
}

export async function updateSupplierBankAccount(
  supplierId: string,
  accountId: string,
  payload: SupplierBankAccountMutation,
): Promise<SupplierBankAccountRecord> {
  const response = await httpClient.put<ApiEnvelope<RawRecord>>(
    `/suppliers/${supplierId}/bank-accounts/${accountId}`,
    mapBankAccountPayload(payload),
  );
  return mapBankAccount(unwrap(response.data, "供应商银行账户更新接口返回了无效数据"));
}

export async function disableSupplierBankAccount(
  supplierId: string,
  accountId: string,
): Promise<SupplierBankAccountRecord> {
  const response = await httpClient.post<ApiEnvelope<RawRecord>>(
    `/suppliers/${supplierId}/bank-accounts/${accountId}/disable`,
  );
  return mapBankAccount(unwrap(response.data, "供应商银行账户停用接口返回了无效数据"));
}

/** 读取包含审核状态的供应商附件。 */
export async function fetchSupplierAttachments(
  supplierId: string,
): Promise<SupplierAttachmentRecord[]> {
  const response = await httpClient.get<ApiEnvelope<RawRecord[]>>(
    `/suppliers/${supplierId}/attachments`,
  );
  return unwrap(response.data, "供应商附件接口返回了无效数据").map(mapAttachment);
}

export async function createSupplierAttachment(
  supplierId: string,
  payload: SupplierAttachmentMutationInput,
): Promise<SupplierAttachmentRecord> {
  const response = await httpClient.post<ApiEnvelope<RawRecord>>(
    `/suppliers/${supplierId}/attachments`,
    mapAttachmentPayload(payload),
  );
  return mapAttachment(unwrap(response.data, "供应商附件创建接口返回了无效数据"));
}

export async function updateSupplierAttachment(
  supplierId: string,
  attachmentId: string,
  payload: SupplierAttachmentMutationInput,
): Promise<SupplierAttachmentRecord> {
  const response = await httpClient.put<ApiEnvelope<RawRecord>>(
    `/suppliers/${supplierId}/attachments/${attachmentId}`,
    mapAttachmentPayload(payload),
  );
  return mapAttachment(unwrap(response.data, "供应商附件更新接口返回了无效数据"));
}

export async function obsoleteSupplierAttachment(
  supplierId: string,
  attachmentId: string,
): Promise<SupplierAttachmentRecord> {
  const response = await httpClient.post<ApiEnvelope<RawRecord>>(
    `/suppliers/${supplierId}/attachments/${attachmentId}/obsolete`,
  );
  return mapAttachment(unwrap(response.data, "供应商附件作废接口返回了无效数据"));
}

export async function reviewSupplierAttachment(
  supplierId: string,
  attachmentId: string,
  attachmentStatus: "valid" | "expired" | "obsolete",
): Promise<SupplierAttachmentRecord> {
  const response = await httpClient.post<ApiEnvelope<RawRecord>>(
    `/suppliers/${supplierId}/attachments/${attachmentId}/review`,
    { attachment_status: attachmentStatus },
  );
  return mapAttachment(unwrap(response.data, "供应商附件审核接口返回了无效数据"));
}

export async function fetchSupplierQuoteCapability(
  supplierId: string,
): Promise<SupplierQuoteCapabilityRecord | null> {
  const response = await httpClient.get<ApiEnvelope<RawRecord | null>>(
    `/suppliers/${supplierId}/quote-capabilities`,
  );
  const data = unwrapNullable(response.data, "供应商报价能力接口返回了无效数据");
  return data ? mapQuoteCapability(data) : null;
}

export async function saveSupplierQuoteCapability(
  supplierId: string,
  payload: SupplierQuoteCapabilityMutation,
): Promise<SupplierQuoteCapabilityRecord> {
  const response = await httpClient.put<ApiEnvelope<RawRecord>>(
    `/suppliers/${supplierId}/quote-capabilities`,
    mapQuotePayload(payload),
  );
  return mapQuoteCapability(unwrap(response.data, "供应商报价能力保存接口返回了无效数据"));
}

export async function fetchSupplierBusCapabilities(
  supplierId: string,
): Promise<SupplierBusCapabilityRecord[]> {
  const response = await httpClient.get<ApiEnvelope<RawRecord[]>>(
    `/suppliers/${supplierId}/bus-service-capabilities`,
  );
  return unwrap(response.data, "车队服务能力接口返回了无效数据").map(mapBusCapability);
}

export async function createSupplierBusCapability(
  supplierId: string,
  payload: SupplierBusCapabilityMutation,
): Promise<SupplierBusCapabilityRecord> {
  const response = await httpClient.post<ApiEnvelope<RawRecord>>(
    `/suppliers/${supplierId}/bus-service-capabilities`,
    mapBusCapabilityPayload(payload),
  );
  return mapBusCapability(unwrap(response.data, "车队服务能力创建接口返回了无效数据"));
}

export async function updateSupplierBusCapability(
  supplierId: string,
  capabilityId: string,
  payload: SupplierBusCapabilityMutation,
): Promise<SupplierBusCapabilityRecord> {
  const response = await httpClient.put<ApiEnvelope<RawRecord>>(
    `/suppliers/${supplierId}/bus-service-capabilities/${capabilityId}`,
    mapBusCapabilityPayload(payload),
  );
  return mapBusCapability(unwrap(response.data, "车队服务能力更新接口返回了无效数据"));
}

export async function fetchSupplierGuideRelations(
  supplierId: string,
): Promise<SupplierGuideRelationRecord[]> {
  const response = await httpClient.get<ApiEnvelope<RawRecord[]>>(
    `/suppliers/${supplierId}/guide-provider-relations`,
  );
  return unwrap(response.data, "导游提供关系接口返回了无效数据").map(mapGuideRelation);
}

export async function createSupplierGuideRelation(
  supplierId: string,
  payload: SupplierGuideRelationMutation,
): Promise<SupplierGuideRelationRecord> {
  const response = await httpClient.post<ApiEnvelope<RawRecord>>(
    `/suppliers/${supplierId}/guide-provider-relations`,
    mapGuideRelationPayload(payload),
  );
  return mapGuideRelation(unwrap(response.data, "导游提供关系创建接口返回了无效数据"));
}

export async function updateSupplierGuideRelation(
  supplierId: string,
  relationId: string,
  payload: SupplierGuideRelationMutation,
): Promise<SupplierGuideRelationRecord> {
  const response = await httpClient.put<ApiEnvelope<RawRecord>>(
    `/suppliers/${supplierId}/guide-provider-relations/${relationId}`,
    mapGuideRelationPayload(payload),
  );
  return mapGuideRelation(unwrap(response.data, "导游提供关系更新接口返回了无效数据"));
}

export async function fetchSupplierStayCapabilities(
  supplierId: string,
): Promise<SupplierStayCapabilityRecord[]> {
  const response = await httpClient.get<ApiEnvelope<RawRecord[]>>(
    `/suppliers/${supplierId}/stay-capabilities`,
  );
  return unwrap(response.data, "住宿供应能力接口返回了无效数据").map(mapStayCapability);
}

function unwrap<T>(envelope: ApiEnvelope<T>, message: string): T {
  if (envelope.code !== "OK" || envelope.data === null || envelope.data === undefined) {
    throw new Error(message);
  }
  return envelope.data;
}

function unwrapNullable<T>(envelope: ApiEnvelope<T | null>, message: string): T | null {
  if (envelope.code !== "OK" || envelope.data === undefined) {
    throw new Error(message);
  }
  return envelope.data;
}

function optionalText(value?: string) {
  return value?.trim() || undefined;
}

function mapContactPayload(payload: SupplierContactMutationInput) {
  return {
    contact_name: payload.contactName.trim(),
    contact_role_type: payload.contactRoleType.trim(),
    job_title: optionalText(payload.jobTitle),
    mobile: optionalText(payload.mobile),
    phone: optionalText(payload.phone),
    wechat: optionalText(payload.wechat),
    email: optionalText(payload.email),
    is_default_quote_contact: payload.isDefaultQuoteContact ?? false,
    is_default_settlement_contact: payload.isDefaultSettlementContact ?? false,
    is_default_operation_contact: payload.isDefaultOperationContact ?? false,
    is_default_emergency_contact: payload.isDefaultEmergencyContact ?? false,
    remark: payload.remark.trim(),
  };
}

function mapBankAccountPayload(payload: SupplierBankAccountMutation) {
  return {
    account_name: payload.accountName.trim(),
    bank_name: payload.bankName.trim(),
    bank_branch_name: optionalText(payload.bankBranchName),
    bank_account_no: payload.bankAccountNo.trim(),
    account_type: payload.accountType,
    usage_type: payload.usageType,
    is_default_payee: payload.isDefaultPayee,
    is_default_refund_account: payload.isDefaultRefundAccount,
    effective_from: optionalText(payload.effectiveFrom),
    effective_to: optionalText(payload.effectiveTo),
    remark: payload.remark.trim(),
  };
}

function mapAttachmentPayload(payload: SupplierAttachmentMutationInput) {
  return {
    attachment_type: payload.attachmentType.trim(),
    attachment_name: payload.attachmentName.trim(),
    file_url: payload.fileUrl.trim(),
    content_text: optionalText(payload.contentText),
    sort_no: payload.sortNo,
    effective_from: optionalText(payload.effectiveFrom),
    effective_to: optionalText(payload.effectiveTo),
    remark: payload.remark.trim(),
  };
}

function mapQuotePayload(payload: SupplierQuoteCapabilityMutation) {
  return {
    can_quote_hotel: payload.canQuoteHotel,
    can_quote_bus: payload.canQuoteBus,
    can_quote_ticket: payload.canQuoteTicket,
    can_quote_guide: payload.canQuoteGuide,
    can_quote_meal: payload.canQuoteMeal,
    hotel_area_text: optionalText(payload.hotelAreaText),
    bus_area_text: optionalText(payload.busAreaText),
    ticket_area_text: optionalText(payload.ticketAreaText),
    guide_area_text: optionalText(payload.guideAreaText),
    meal_area_text: optionalText(payload.mealAreaText),
    capability_remark: optionalText(payload.capabilityRemark),
    quote_priority_level: payload.quotePriorityLevel,
    is_enabled: payload.isEnabled,
    remark: payload.remark.trim(),
  };
}

function mapTimedPayload(payload: SupplierTimedCapabilityMutation) {
  return {
    cooperation_status: payload.cooperationStatus,
    valid_from: optionalText(payload.validFrom),
    valid_to: optionalText(payload.validTo),
    evidence_type: optionalText(payload.evidenceType),
    evidence_id: optionalText(payload.evidenceId),
    evidence_summary: optionalText(payload.evidenceSummary),
    review_status: payload.reviewStatus,
    remark: payload.remark.trim(),
  };
}

function mapBusCapabilityPayload(payload: SupplierBusCapabilityMutation) {
  return {
    ...mapTimedPayload(payload),
    country: optionalText(payload.country),
    province: optionalText(payload.province),
    city: optionalText(payload.city),
    district: optionalText(payload.district),
    vehicle_type: optionalText(payload.vehicleType),
    seat_count_min: payload.seatCountMin,
    seat_count_max: payload.seatCountMax,
    service_mode: optionalText(payload.serviceMode),
  };
}

function mapGuideRelationPayload(payload: SupplierGuideRelationMutation) {
  return {
    ...mapTimedPayload(payload),
    provider_supplier_id: payload.providerSupplierId.trim(),
    guide_supplier_id: payload.guideSupplierId.trim(),
    relation_type: payload.relationType,
  };
}

function textValue(raw: RawRecord, key: string, fallback = "") {
  const value = raw[key];
  return typeof value === "string" ? value : fallback;
}

function optionalString(raw: RawRecord, key: string) {
  const value = raw[key];
  return typeof value === "string" ? value : undefined;
}

function boolValue(raw: RawRecord, key: string) {
  return raw[key] === true;
}

function numberValue(raw: RawRecord, key: string) {
  const value = raw[key];
  return typeof value === "number" ? value : undefined;
}

function mapContact(raw: RawRecord): SupplierContactRecord {
  return {
    supplierContactId: optionalString(raw, "supplier_contact_id"),
    supplierId: optionalString(raw, "supplier_id"),
    contactName: textValue(raw, "contact_name"),
    contactRoleType: textValue(raw, "contact_role_type", "other"),
    jobTitle: optionalString(raw, "job_title"),
    mobile: optionalString(raw, "mobile"),
    phone: optionalString(raw, "phone"),
    wechat: optionalString(raw, "wechat"),
    email: optionalString(raw, "email"),
    isDefaultQuoteContact: boolValue(raw, "is_default_quote_contact"),
    isDefaultSettlementContact: boolValue(raw, "is_default_settlement_contact"),
    isDefaultOperationContact: boolValue(raw, "is_default_operation_contact"),
    isDefaultEmergencyContact: boolValue(raw, "is_default_emergency_contact"),
    contactStatus: textValue(raw, "contact_status", "active"),
    isLegacy: boolValue(raw, "is_legacy"),
    createdAt: optionalString(raw, "created_at"),
    updatedAt: optionalString(raw, "updated_at"),
    remark: textValue(raw, "remark"),
  };
}

function mapBankAccount(raw: RawRecord): SupplierBankAccountRecord {
  return {
    supplierBankAccountId: textValue(raw, "supplier_bank_account_id"),
    supplierId: textValue(raw, "supplier_id"),
    accountName: textValue(raw, "account_name"),
    bankName: textValue(raw, "bank_name"),
    bankBranchName: optionalString(raw, "bank_branch_name"),
    bankAccountNoMasked: textValue(raw, "bank_account_no_masked"),
    accountType: textValue(raw, "account_type", "public") as SupplierBankAccountMutation["accountType"],
    usageType: textValue(raw, "usage_type", "payee") as SupplierBankAccountMutation["usageType"],
    isDefaultPayee: boolValue(raw, "is_default_payee"),
    isDefaultRefundAccount: boolValue(raw, "is_default_refund_account"),
    accountStatus: textValue(raw, "account_status", "active"),
    effectiveFrom: optionalString(raw, "effective_from"),
    effectiveTo: optionalString(raw, "effective_to"),
    createdAt: textValue(raw, "created_at"),
    updatedAt: textValue(raw, "updated_at"),
    remark: textValue(raw, "remark"),
  };
}

function mapAttachment(raw: RawRecord): SupplierAttachmentRecord {
  return {
    supplierAttachmentId: optionalString(raw, "supplier_attachment_id"),
    attachmentType: textValue(raw, "attachment_type"),
    attachmentName: textValue(raw, "attachment_name"),
    fileUrl: textValue(raw, "file_url"),
    contentText: optionalString(raw, "content_text"),
    sortNo: numberValue(raw, "sort_no"),
    effectiveFrom: optionalString(raw, "effective_from"),
    effectiveTo: optionalString(raw, "effective_to"),
    mediaScene: optionalString(raw, "media_scene"),
    attachmentStatus: textValue(raw, "attachment_status", "pending_review"),
    isLegacy: boolValue(raw, "is_legacy"),
    createdAt: optionalString(raw, "created_at"),
    updatedAt: optionalString(raw, "updated_at"),
    remark: textValue(raw, "remark"),
  };
}

function mapQuoteCapability(raw: RawRecord): SupplierQuoteCapabilityRecord {
  return {
    supplierQuoteCapabilityId: textValue(raw, "supplier_quote_capability_id"),
    supplierId: textValue(raw, "supplier_id"),
    canQuoteHotel: boolValue(raw, "can_quote_hotel"),
    canQuoteBus: boolValue(raw, "can_quote_bus"),
    canQuoteTicket: boolValue(raw, "can_quote_ticket"),
    canQuoteGuide: boolValue(raw, "can_quote_guide"),
    canQuoteMeal: boolValue(raw, "can_quote_meal"),
    hotelAreaText: optionalString(raw, "hotel_area_text"),
    busAreaText: optionalString(raw, "bus_area_text"),
    ticketAreaText: optionalString(raw, "ticket_area_text"),
    guideAreaText: optionalString(raw, "guide_area_text"),
    mealAreaText: optionalString(raw, "meal_area_text"),
    capabilityRemark: optionalString(raw, "capability_remark"),
    quotePriorityLevel: numberValue(raw, "quote_priority_level"),
    isEnabled: boolValue(raw, "is_enabled"),
    createdAt: textValue(raw, "created_at"),
    updatedAt: textValue(raw, "updated_at"),
    remark: textValue(raw, "remark"),
  };
}

function mapTimed(raw: RawRecord): SupplierTimedCapabilityMutation {
  return {
    cooperationStatus: textValue(raw, "cooperation_status", "candidate") as SupplierTimedCapabilityMutation["cooperationStatus"],
    validFrom: optionalString(raw, "valid_from"),
    validTo: optionalString(raw, "valid_to"),
    evidenceType: optionalString(raw, "evidence_type"),
    evidenceId: optionalString(raw, "evidence_id"),
    evidenceSummary: optionalString(raw, "evidence_summary"),
    reviewStatus: textValue(raw, "review_status", "pending") as SupplierTimedCapabilityMutation["reviewStatus"],
    remark: textValue(raw, "remark"),
  };
}

function mapBusCapability(raw: RawRecord): SupplierBusCapabilityRecord {
  return {
    ...mapTimed(raw),
    supplierBusServiceCapabilityId: textValue(raw, "supplier_bus_service_capability_id"),
    supplierId: textValue(raw, "supplier_id"),
    country: optionalString(raw, "country"),
    province: optionalString(raw, "province"),
    city: optionalString(raw, "city"),
    district: optionalString(raw, "district"),
    vehicleType: optionalString(raw, "vehicle_type"),
    seatCountMin: numberValue(raw, "seat_count_min"),
    seatCountMax: numberValue(raw, "seat_count_max"),
    serviceMode: optionalString(raw, "service_mode"),
    createdAt: textValue(raw, "created_at"),
    updatedAt: textValue(raw, "updated_at"),
  };
}

function mapGuideRelation(raw: RawRecord): SupplierGuideRelationRecord {
  return {
    ...mapTimed(raw),
    supplierGuideProviderRelationId: textValue(raw, "supplier_guide_provider_relation_id"),
    providerSupplierId: textValue(raw, "provider_supplier_id"),
    guideSupplierId: textValue(raw, "guide_supplier_id"),
    relationType: textValue(raw, "relation_type", "other") as SupplierGuideRelationMutation["relationType"],
    createdAt: textValue(raw, "created_at"),
    updatedAt: textValue(raw, "updated_at"),
  };
}

function mapStayCapability(raw: RawRecord): SupplierStayCapabilityRecord {
  return {
    ...mapTimed(raw),
    resourceStaySupplierCapabilityId: textValue(raw, "resource_stay_supplier_capability_id"),
    staySpuId: textValue(raw, "stay_spu_id"),
    staySkuId: optionalString(raw, "stay_sku_id"),
    supplierId: textValue(raw, "supplier_id"),
    hotelName: textValue(raw, "hotel_name"),
    procurementChannelType: textValue(raw, "procurement_channel_type"),
  };
}
